import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApiClient, PageDto } from "../api/client";
import { Campaign, CampaignRecipient, campaignApi } from "../api/campaigns";
import { PageHeader, PageShell, StatusBadge } from "../components/ui";
import {
  CampaignPager,
  CampaignRecipientTable,
  campaignDate,
  campaignName,
  campaignRate,
  campaignRates,
} from "../components/CampaignUi";

const statuses: Record<string, string> = {
  "": "Alle Empfänger",
  QUEUED: "Wartend",
  ACCEPTED: "Angenommen",
  DELIVERED: "Zugestellt",
  OPENED: "Geöffnet",
  CLICKED: "Geklickt",
  UNSUBSCRIBED: "Abgemeldet",
  FAILED: "Fehlgeschlagen",
  SUPPRESSED: "Ausgeschlossen",
  CANCELLED: "Abgebrochen",
};

export const CampaignDetailsPage: React.FC = () => {
  const { campaignId } = useParams();
  const { request } = useApiClient();
  const api = useMemo(() => campaignApi(request), [request]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");
  const [recipients, setRecipients] =
    useState<PageDto<CampaignRecipient> | null>(null);
  const [recipientError, setRecipientError] = useState("");
  const [html, setHtml] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [campaignId]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(campaignId!)
      .then((c) => {
        if (!cancelled) setCampaign(c);
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            (e as Error).message || "Kampagne konnte nicht geladen werden."
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaignId, revision]);
  useEffect(() => {
    setPage(0);
    setStatus("");
    setShowMessage(false);
  }, [campaignId]);
  useEffect(() => {
    setRecipients(null);
    setRecipientError("");
    if (!campaign || campaign.draft || campaign.id !== campaignId) return;
    let cancelled = false;
    api
      .recipients(campaign.id, page, status)
      .then((r) => {
        if (!cancelled) setRecipients(r);
      })
      .catch((e) => {
        if (!cancelled)
          setRecipientError(
            (e as Error).message || "Empfänger konnten nicht geladen werden."
          );
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaign, campaignId, page, status]);
  useEffect(() => {
    setHtml("");
    setPreviewError("");
    if (!showMessage || !campaign || campaign.channel !== "EMAIL") return;
    let cancelled = false;
    api
      .preview(campaign)
      .then((result) => {
        if (!cancelled) setHtml(result.html);
      })
      .catch((e) => {
        if (!cancelled)
          setPreviewError(
            (e as Error).message || "Nachricht konnte nicht geladen werden."
          );
      });
    return () => {
      cancelled = true;
    };
  }, [api, campaign, showMessage]);

  return (
    <PageShell>
      <Link
        className="mb-5 inline-flex text-sm font-medium text-gray-500 hover:text-secondary"
        to="/notifications"
      >
        ← Alle Kampagnen
      </Link>
      <PageHeader
        title={
          campaign && campaign.id === campaignId
            ? campaignName(campaign)
            : "Kampagnendetails"
        }
        description={
          campaign
            ? `${campaign.channel === "EMAIL" ? "E-Mail" : "Push"} · ${
                campaign.draft ? "Erstellt" : "Versand gestartet"
              } am ${campaignDate(campaign.sentAt || campaign.createdAt)}`
            : undefined
        }
        actions={
          <button
            className="btn btn-neutral"
            disabled={loading}
            onClick={() => setRevision((r) => r + 1)}
          >
            Aktualisieren
          </button>
        }
      />
      {error ? (
        <p role="alert" className="rounded-xl bg-red-50 p-5 text-red-700">
          {error}
        </p>
      ) : !campaign || campaign.id !== campaignId ? (
        <p role="status" className="p-8 text-gray-500">
          Kampagne wird geladen …
        </p>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <StatusBadge tone={campaign.draft ? "amber" : "green"}>
              {campaign.draft
                ? "Entwurf"
                : campaign.metrics.queued
                ? "Versand läuft"
                : "Versand gestartet"}
            </StatusBadge>
            <span className="text-sm text-gray-500">
              {campaign.draft
                ? "Noch nicht versendet"
                : `${campaign.metrics.targeted} Empfänger in der Zielgruppe · ${campaign.metrics.excluded} ausgeschlossen`}
            </span>
          </div>
          {campaign.userGroup === "LEADS" && <p className="mb-6 text-sm text-gray-600">Zielgruppe: Leads ohne Umfrage · bestehende mobie-Nutzer ausgeschlossen</p>}
          {campaign.userGroup === "SURVEY_LEADS" && <p className="mb-6 text-sm text-gray-600">Zielgruppe: Teilnehmer der Umfrage „{campaign.surveyTitle || campaign.surveyId}“ · mit und ohne mobie-Konto</p>}
          {campaign.draft ? (
            <section className="rounded-2xl border border-gray-200 bg-white p-6">
              <h2 className="font-semibold">
                Diese Kampagne ist noch ein Entwurf.
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Nach dem Versand findest du hier die Ergebnisse und den Status
                jedes Empfängers.
              </p>
              <Link
                className="btn btn-primary mt-4"
                to={`/notifications/${encodeURIComponent(campaign.id)}/edit`}
              >
                Entwurf bearbeiten
              </Link>
            </section>
          ) : (
            <>
              <section
                aria-label="Wichtigste Kennzahlen"
                className={`grid gap-4 ${
                  campaign.channel === "EMAIL"
                    ? "md:grid-cols-3"
                    : "md:max-w-lg"
                }`}
              >
                {campaignRates(campaign)
                  .slice(0, campaign.channel === "EMAIL" ? 3 : 1)
                  .map((kpi, index) => (
                    <div
                      key={kpi.label}
                      className={`rounded-2xl border p-6 ${
                        index === 0
                          ? "border-secondary/20 bg-secondary/5"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <h2 className="text-sm font-semibold text-gray-600">
                        {kpi.label}
                      </h2>
                      <p
                        className={`mt-4 text-5xl font-semibold tabular-nums tracking-tight ${
                          index === 0 ? "text-secondary" : "text-gray-900"
                        }`}
                      >
                        {campaignRate(kpi.value)}
                      </p>
                      <p className="mt-4 text-xs text-gray-500">{kpi.detail}</p>
                    </div>
                  ))}
              </section>
              <section
                aria-label="Versandstatus"
                className="mt-6 rounded-2xl border border-gray-200 bg-white p-6"
              >
                <h2 className="mb-4 text-base font-semibold text-gray-900">
                  Versand im Überblick
                </h2>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 xl:grid-cols-6">
                  {(
                    [
                      ["Wartend", "queued"],
                      [
                        campaign.channel === "PUSH"
                          ? "FCM-Annahme"
                          : "Angenommen",
                        "accepted",
                      ],
                      ["Zugestellt", "delivered"],
                      ["Fehlgeschlagen", "failed"],
                      ["Abgemeldet", "unsubscribed"],
                      ["Beschwerden", "complained"],
                    ] as const
                  )
                    .filter(
                      ([, key]) =>
                        campaign.channel === "EMAIL" ||
                        !["delivered", "unsubscribed", "complained"].includes(
                          key
                        )
                    )
                    .map(([label, key]) => (
                      <div key={key}>
                        <dt className="text-xs text-gray-500">{label}</dt>
                        <dd className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                          {campaign.metrics[key]}
                        </dd>
                      </div>
                    ))}
                </dl>
                <p className="mt-5 border-t border-gray-100 pt-4 text-xs leading-relaxed text-gray-500">
                  {campaign.channel === "PUSH"
                    ? "FCM-Annahme bestätigt den Versand. Geräte-Zustellung: nicht erfasst. Öffnungen zählen Taps auf die Nachricht."
                    : "Die Öffnungsrate zählt eindeutige Empfänger mit erfasster Öffnung, auch bei unbekanntem Client. Erkannte Bots zählen nicht. Öffnungen sind technisch gemessene Signale und bestätigen nicht, dass die Mail gelesen wurde. Die Klickrate zählt nur regulär klassifizierte Klicks."}
                </p>
              </section>
              {campaign.channel === "EMAIL" && (
                <details className="mt-4 rounded-xl border border-gray-200 bg-white px-5 py-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-600">
                    Weitere Messwerte · Bots, unbekannte Clients & Abmeldungen
                  </summary>
                  <dl className="mt-4 grid gap-3 border-t border-gray-100 pt-4 text-sm sm:grid-cols-2 xl:grid-cols-3">
                    {(
                      [
                        ["Bot-Öffnungen", "botOpened"],
                        ["Unbekannte Öffnungen", "unknownOpened"],
                        ["Bot-Klicks", "botClicked"],
                        ["Unbekannte Klicks", "unknownClicked"],
                      ] as const
                    ).map(([label, key]) => (
                      <div key={key} className="flex justify-between gap-3">
                        <dt className="text-gray-500">{label}</dt>
                        <dd className="font-medium tabular-nums">
                          {campaign.metrics[key]}
                        </dd>
                      </div>
                    ))}
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">Abmelderate</dt>
                      <dd className="font-medium">
                        {campaignRate(campaign.metrics.unsubscribeRate)}
                      </dd>
                    </div>
                  </dl>
                </details>
              )}
              <section className="mt-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Empfänger & Ereignisse
                    </h2>
                    <p className="mt-1 text-xs text-gray-500">
                      Status und Verlauf für jeden Nutzer.
                    </p>
                  </div>
                  <label className="text-sm text-gray-600">
                    <span className="sr-only">Empfänger filtern</span>
                    <select
                      className="form-control"
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(0);
                      }}
                    >
                      {Object.entries(statuses).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {recipientError ? (
                  <p role="alert" className="text-sm text-red-700">
                    {recipientError}
                  </p>
                ) : recipients ? (
                  <>
                    <CampaignRecipientTable rows={recipients.content} />
                    {recipients.totalPages > 1 && (
                      <CampaignPager
                        page={page}
                        last={recipients.last}
                        onPage={setPage}
                      />
                    )}
                  </>
                ) : (
                  <p role="status" className="py-6 text-sm text-gray-500">
                    Empfänger werden geladen …
                  </p>
                )}
              </section>
            </>
          )}
          <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 sm:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">
                  {campaign.draft ? "Nachricht" : "Versendete Nachricht"}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {campaign.subject || campaign.title}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-neutral"
                aria-expanded={showMessage}
                onClick={() => setShowMessage((s) => !s)}
              >
                {showMessage ? "Nachricht ausblenden" : "Nachricht ansehen"}
              </button>
            </div>
            {showMessage &&
              (campaign.channel === "EMAIL" ? (
                previewError ? (
                  <p role="alert" className="mt-4 text-sm text-red-700">
                    {previewError}
                  </p>
                ) : html ? (
                  <iframe
                    title="Versendete E-Mail"
                    sandbox=""
                    srcDoc={html}
                    className="mx-auto mt-5 h-[640px] w-full max-w-2xl rounded-xl border"
                  />
                ) : (
                  <p role="status" className="mt-4 text-sm text-gray-500">
                    Nachricht wird geladen …
                  </p>
                )
              ) : (
                <div className="mt-5 rounded-xl bg-gray-50 p-5">
                  <strong>{campaign.title}</strong>
                  <p className="mt-2 whitespace-pre-line">{campaign.body}</p>
                </div>
              ))}
          </section>
        </>
      )}
    </PageShell>
  );
};
